import { z } from 'zod';
import { ZipRepositoryFileNameSchema } from './zip-repository-file-name.schema';

const MIB = 1024 * 1024;

export const SOURCE_CODE_UPLOAD_LIMITS = {
  singlePutMaxBytes: 100 * MIB,
  defaultPartBytes: 16 * MIB,
  maxPartCount: 10_000,
  maxFileBytes: 10 * 1024 * MIB,
  maxPartsPerSignRequest: 20,
} as const;

export const SOURCE_CODE_UPLOAD_STRATEGIES = {
  SINGLE_PUT: 'SINGLE_PUT',
  MULTIPART: 'MULTIPART',
} as const;

const MultipartUploadIdSchema = z.string().min(1).max(1024);

const PartNumberSchema = z
  .int()
  .min(1)
  .max(SOURCE_CODE_UPLOAD_LIMITS.maxPartCount);

export const InitiateSourceCodeUploadBodySchema = z.object({
  fileName: ZipRepositoryFileNameSchema,
  fileSize: z
    .int()
    .positive()
    .max(
      SOURCE_CODE_UPLOAD_LIMITS.maxFileBytes,
      'ZIP file exceeds the 10 GiB per-repository limit',
    ),
});

export const InitiateSourceCodeUploadResultSchema = z.discriminatedUnion(
  'strategy',
  [
    z.object({
      strategy: z.literal(SOURCE_CODE_UPLOAD_STRATEGIES.SINGLE_PUT),
      key: z.string(),
      uploadUrl: z.string(),
    }),
    z.object({
      strategy: z.literal(SOURCE_CODE_UPLOAD_STRATEGIES.MULTIPART),
      key: z.string(),
      uploadId: MultipartUploadIdSchema,
      partSize: z.int().positive(),
      partCount: PartNumberSchema,
    }),
  ],
);

export const SignSourceCodeUploadPartsBodySchema = z.object({
  fileName: ZipRepositoryFileNameSchema,
  uploadId: MultipartUploadIdSchema,
  partNumbers: z
    .array(PartNumberSchema)
    .min(1)
    .max(SOURCE_CODE_UPLOAD_LIMITS.maxPartsPerSignRequest),
});

export const SignSourceCodeUploadPartsResultSchema = z.object({
  parts: z.array(
    z.object({
      partNumber: PartNumberSchema,
      uploadUrl: z.string(),
    }),
  ),
});

export const CompleteSourceCodeUploadBodySchema = z.object({
  fileName: ZipRepositoryFileNameSchema,
  uploadId: MultipartUploadIdSchema,
  partCount: PartNumberSchema,
});

export const CompleteSourceCodeUploadResultSchema = z.object({
  key: z.string(),
});

export const AbortSourceCodeUploadBodySchema = z.object({
  fileName: ZipRepositoryFileNameSchema,
  uploadId: MultipartUploadIdSchema,
});

export type InitiateSourceCodeUploadBody = z.infer<
  typeof InitiateSourceCodeUploadBodySchema
>;
export type InitiateSourceCodeUploadResult = z.infer<
  typeof InitiateSourceCodeUploadResultSchema
>;
export type SignSourceCodeUploadPartsBody = z.infer<
  typeof SignSourceCodeUploadPartsBodySchema
>;
export type SignSourceCodeUploadPartsResult = z.infer<
  typeof SignSourceCodeUploadPartsResultSchema
>;
export type CompleteSourceCodeUploadBody = z.infer<
  typeof CompleteSourceCodeUploadBodySchema
>;
export type AbortSourceCodeUploadBody = z.infer<
  typeof AbortSourceCodeUploadBodySchema
>;
